import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Tag,
  Space,
  Dropdown,
  message,
  Statistic,
  Select,
  DatePicker,
  Divider,
  Typography,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import useStore from '../store/useStore';
import { purchaseOrdersAPI, vendorsAPI, productsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const PurchaseOrders = () => {
  const { purchaseOrders, setPurchaseOrders } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedPO, setSelectedPO] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchaseOrdersAPI.getAll();
      setPurchaseOrders(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setPurchaseOrders]);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await vendorsAPI.getAll();
      setVendors(response.data);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const handleAddPO = () => {
    setEditingPO(null);
    form.resetFields();
    form.setFieldsValue({
      orderDate: moment(),
      expectedDate: moment().add(7, 'days'),
      status: 'draft',
    });
    setDrawerVisible(true);
  };

  const handleEditPO = (po) => {
    setEditingPO(po);
    form.setFieldsValue({
      ...po,
      orderDate: po.orderDate ? moment(po.orderDate) : null,
      expectedDate: po.expectedDate ? moment(po.expectedDate) : null,
    });
    setDrawerVisible(true);
  };

  const handleViewPO = (po) => {
    setSelectedPO(po);
    setCurrentView('detail');
  };

  const handleDeletePO = async (poId) => {
    try {
      await purchaseOrdersAPI.delete(poId);
      message.success('Purchase order deleted successfully');
      await fetchPurchaseOrders();
    } catch (err) {
      message.error('Failed to delete purchase order');
    }
  };

  const handleSendPO = async (poId) => {
    try {
      await purchaseOrdersAPI.send(poId);
      message.success('Purchase order sent successfully');
      await fetchPurchaseOrders();
    } catch (err) {
      message.error('Failed to send purchase order');
    }
  };

  const handleReceivePO = async (poId) => {
    try {
      await purchaseOrdersAPI.receive(poId);
      message.success('Purchase order marked as received');
      await fetchPurchaseOrders();
    } catch (err) {
      message.error('Failed to receive purchase order');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingPO(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        orderDate: values.orderDate ? values.orderDate.format('YYYY-MM-DD') : null,
        expectedDate: values.expectedDate ? values.expectedDate.format('YYYY-MM-DD') : null,
      };

      if (editingPO) {
        await purchaseOrdersAPI.update(editingPO.id, formattedValues);
        message.success('Purchase order updated successfully');
      } else {
        await purchaseOrdersAPI.create({ ...formattedValues, total: 0 });
        message.success('Purchase order created successfully');
      }

      await fetchPurchaseOrders();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save purchase order');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedPO(null);
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  const getPOStatus = (po) => {
    switch (po.status) {
      case 'draft':
        return { color: 'default', text: 'Draft', icon: <EditOutlined /> };
      case 'sent':
        return { color: 'blue', text: 'Sent', icon: <TruckOutlined /> };
      case 'confirmed':
        return { color: 'orange', text: 'Confirmed', icon: <CheckCircleOutlined /> };
      case 'partial':
        return { color: 'yellow', text: 'Partially Received', icon: <ClockCircleOutlined /> };
      case 'received':
        return { color: 'green', text: 'Received', icon: <CheckCircleOutlined /> };
      case 'cancelled':
        return { color: 'red', text: 'Cancelled', icon: <CloseCircleOutlined /> };
      default:
        return { color: 'default', text: po.status, icon: <ClockCircleOutlined /> };
    }
  };

  const getReceivingProgress = (po) => {
    if (!po.items || po.items.length === 0) return 0;
    const totalItems = po.items.reduce((sum, item) => sum + item.quantity, 0);
    const receivedItems = po.items.reduce((sum, item) => sum + (item.receivedQuantity || 0), 0);
    return totalItems > 0 ? Math.round((receivedItems / totalItems) * 100) : 0;
  };

  const filteredPOs = purchaseOrders.filter(po => {
    const matchesSearch = po.poNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
                         po.vendorName?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber',
      render: (poNumber, record) => (
        <div>
          <div className="font-medium text-blue-600 cursor-pointer hover:underline"
               onClick={() => handleViewPO(record)}>
            {poNumber}
          </div>
          <div className="text-sm text-gray-500">
            {moment(record.orderDate).format('MMM DD, YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
      key: 'vendorName',
      render: (vendorName, record) => (
        <div>
          <div className="font-medium">{vendorName}</div>
          <div className="text-sm text-gray-500">{record.vendorEmail}</div>
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => (
        <div className="font-medium">${total?.toFixed(2)}</div>
      ),
    },
    {
      title: 'Expected Date',
      dataIndex: 'expectedDate',
      key: 'expectedDate',
      render: (expectedDate) => {
        const isOverdue = moment(expectedDate).isBefore(moment()) && 
                         !['received', 'cancelled'].includes(expectedDate.status);
        return (
          <div className={isOverdue ? 'text-red-500' : ''}>
            {moment(expectedDate).format('MMM DD, YYYY')}
            {isOverdue && <div className="text-xs">Overdue</div>}
          </div>
        );
      },
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const progress = getReceivingProgress(record);
        return (
          <div className="w-20">
            <Progress
              percent={progress}
              size="small"
              status={progress === 100 ? 'success' : 'active'}
            />
            <div className="text-xs text-gray-500 mt-1">{progress}%</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        const status = getPOStatus(record);
        return (
          <Tag color={status.color} icon={status.icon}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            label: 'View PO',
            icon: <FileTextOutlined />,
            onClick: () => handleViewPO(record),
          },
          {
            key: 'edit',
            label: 'Edit PO',
            icon: <EditOutlined />,
            onClick: () => handleEditPO(record),
          },
        ];

        if (record.status === 'draft') {
          menuItems.push({
            key: 'send',
            label: 'Send PO',
            icon: <TruckOutlined />,
            onClick: () => handleSendPO(record.id),
          });
        }

        if (['sent', 'confirmed', 'partial'].includes(record.status)) {
          menuItems.push({
            key: 'receive',
            label: 'Mark as Received',
            icon: <CheckCircleOutlined />,
            onClick: () => handleReceivePO(record.id),
          });
        }

        menuItems.push({
          key: 'delete',
          label: 'Delete PO',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => handleDeletePO(record.id),
        });

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  useEffect(() => {
    fetchPurchaseOrders();
    fetchVendors();
    fetchProducts();
  }, [fetchPurchaseOrders, fetchVendors, fetchProducts]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading purchase orders..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchPurchaseOrders}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Purchase Orders</h1>
          <p className="text-gray-600">Manage purchase orders and vendor relationships</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddPO}
          className="w-full sm:w-auto"
        >
          Create Purchase Order
        </Button>
      </div>

      {/* Stats Cards */}
      {currentView === 'list' && (
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total POs"
                value={purchaseOrders.length}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Pending"
                value={purchaseOrders.filter(po => ['sent', 'confirmed'].includes(po.status)).length}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Received"
                value={purchaseOrders.filter(po => po.status === 'received').length}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Value"
                value={purchaseOrders.reduce((sum, po) => sum + (po.total || 0), 0)}
                prefix={<DollarOutlined />}
                precision={2}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Search and Filters + Table / Detail View */}
      <Card>
        {currentView === 'list' ? (
          <>
            <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-between">
              <div className="flex gap-4">
                <Search
                  placeholder="Search purchase orders..."
                  allowClear
                  onSearch={handleSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="sm:w-80"
                />
                <Select
                  placeholder="Filter by status"
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  className="w-full sm:w-40"
                >
                  <Option value="all">All Status</Option>
                  <Option value="draft">Draft</Option>
                  <Option value="sent">Sent</Option>
                  <Option value="confirmed">Confirmed</Option>
                  <Option value="partial">Partial</Option>
                  <Option value="received">Received</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={filteredPOs}
              rowKey="id"
              rowSelection={rowSelection}
              onRow={(record) => ({
                onClick: () => handleViewPO(record),
                className: 'cursor-pointer hover:bg-gray-50',
              })}
              pagination={{
                total: filteredPOs.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} purchase orders`,
              }}
              scroll={{ x: 800 }}
            />
          </>
        ) : (
          selectedPO && (
            <DetailView
              title={`Purchase Order #${selectedPO.poNumber}`}
              subtitle={`${selectedPO.vendorName || ''} • ${moment(selectedPO.orderDate).format('MMM DD, YYYY')}`}
              onBack={handleBackToList}
              onEdit={() => handleEditPO(selectedPO)}
              onDelete={() => {
                Modal.confirm({
                  title: 'Delete Purchase Order',
                  content: 'Are you sure you want to delete this purchase order?',
                  onOk: () => handleDeletePO(selectedPO.id),
                });
              }}
              tags={[{ label: getPOStatus(selectedPO).text, color: getPOStatus(selectedPO).color }]}
            >
              <DetailSection title="Receiving Progress">
                <Progress percent={getReceivingProgress(selectedPO)} />
              </DetailSection>

              <Row gutter={24}>
                <Col xs={24} lg={16} xl={16}>
                  <DetailSection title="Order Information">
                    <DetailField label="Vendor" value={selectedPO.vendorName} />
                    <DetailField label="Order Date" value={moment(selectedPO.orderDate).format('MMM DD, YYYY')} />
                    <DetailField label="Expected Date" value={moment(selectedPO.expectedDate).format('MMM DD, YYYY')} />
                    <DetailField label="Status" value={getPOStatus(selectedPO).text} />
                    {selectedPO.notes && (
                      <DetailField label="Notes" value={selectedPO.notes} />
                    )}
                  </DetailSection>
                </Col>
                <Col xs={24} lg={8} xl={8}>
                  <DetailSection title="Totals">
                    <DetailField label="Total Amount" value={`$${selectedPO.total?.toFixed(2)}`} />
                  </DetailSection>
                </Col>
              </Row>
            </DetailView>
          )
        )}
      </Card>

      {/* Right Drawer for Add/Edit */}
      <RightDrawer
        title={editingPO ? 'Edit Purchase Order' : 'Create New Purchase Order'}
        open={drawerVisible}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        width={800}
      >
        <Form form={form} layout="vertical" className="mt-2" onFinish={handleDrawerSave} id="drawer-form">
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="vendorId"
                label="Vendor"
                type="select"
                options={vendors.map(vendor => ({ value: vendor.id, label: vendor.name }))}
                rules={[{ required: true, message: 'Please select vendor' }]}
                placeholder="Select vendor"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="poNumber"
                label="PO Number"
                rules={[{ required: true, message: 'Please enter PO number' }]}
                placeholder="Enter PO number"
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <FormField name="orderDate" label="Order Date" type="date" rules={[{ required: true, message: 'Please select order date' }]} />
            </Col>
            <Col span={12}>
              <FormField name="expectedDate" label="Expected Delivery Date" type="date" rules={[{ required: true, message: 'Please select expected date' }]} />
            </Col>
          </Row>

          <FormField name="notes" label="Notes" type="textarea" placeholder="Enter any additional notes" rows={3} />

          <FormField
            name="status"
            label="Status"
            type="select"
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'sent', label: 'Sent' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'partial', label: 'Partially Received' },
              { value: 'received', label: 'Received' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
            rules={[{ required: true, message: 'Please select status' }]}
          />
        </Form>
      </RightDrawer>

      
    </div>
  );
};

export default PurchaseOrders;
